import { useState } from "react";
import { Bookmark, BookmarkCheck, Check, Loader2, Navigation, Plus } from "lucide-react";

export type DiscoveryActionsCopy = {
  openMaps: string;
  opening: string;
  openedMaps: string;
  save: string;
  saved: string;
  addToRoute: string;
  inRoute: string;
  error: string;
};

const BASE =
  "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors motion-safe:transition-all motion-safe:duration-200 disabled:opacity-70 motion-safe:active:scale-95";
const IDLE = "border-border bg-background/70 text-foreground";
const DONE_SAVED = "border-brand/50 bg-brand/10 text-brand";
const DONE_ROUTE = "border-brand bg-brand text-brand-foreground";

/** Minimum visible loading state so a tap never feels ignored. */
const MIN_PENDING_MS = 220;

async function withMinDelay(run: () => void | Promise<void>) {
  const started = Date.now();
  await run();
  const rest = MIN_PENDING_MS - (Date.now() - started);
  if (rest > 0) await new Promise((r) => setTimeout(r, rest));
}

export function DiscoveryCardActions({
  mapsUrl,
  saved,
  inRoute,
  onToggleSave,
  onToggleRoute,
  copy,
}: {
  mapsUrl?: string | null;
  saved: boolean;
  inRoute: boolean;
  onToggleSave: () => void | Promise<void>;
  onToggleRoute: () => void | Promise<void>;
  copy: DiscoveryActionsCopy;
}) {
  const [pending, setPending] = useState<"maps" | "save" | "route" | null>(null);
  const [mapsOpened, setMapsOpened] = useState(false);

  async function run(key: "maps" | "save" | "route", action: () => void | Promise<void>) {
    if (pending) return;
    setPending(key);
    try {
      await withMinDelay(action);
    } catch {
      const { toast } = await import("sonner");
      toast.error(copy.error);
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {mapsUrl && (
        <button
          type="button"
          disabled={pending !== null}
          aria-live="polite"
          className={`${BASE} ${mapsOpened ? DONE_SAVED : IDLE}`}
          onClick={() =>
            run("maps", async () => {
              window.open(mapsUrl, "_blank", "noopener,noreferrer");
              setMapsOpened(true);
            })
          }
        >
          {pending === "maps" ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : mapsOpened ? (
            <Check className="h-3 w-3" />
          ) : (
            <Navigation className="h-3 w-3" />
          )}
          {pending === "maps" ? copy.opening : mapsOpened ? copy.openedMaps : copy.openMaps}
        </button>
      )}

      <button
        type="button"
        disabled={pending !== null}
        aria-pressed={saved}
        className={`${BASE} ${saved ? DONE_SAVED : IDLE}`}
        onClick={() => run("save", onToggleSave)}
      >
        {pending === "save" ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : saved ? (
          <BookmarkCheck className="h-3 w-3" />
        ) : (
          <Bookmark className="h-3 w-3" />
        )}
        {saved ? `${copy.saved} ✓` : copy.save}
      </button>

      <button
        type="button"
        disabled={pending !== null}
        aria-pressed={inRoute}
        className={`${BASE} ${inRoute ? DONE_ROUTE : IDLE}`}
        onClick={() => run("route", onToggleRoute)}
      >
        {pending === "route" ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : inRoute ? (
          <Check className="h-3 w-3" />
        ) : (
          <Plus className="h-3 w-3" />
        )}
        {inRoute ? `${copy.inRoute} ✓` : copy.addToRoute}
      </button>
    </div>
  );
}
